import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";

/**
 * Global cart state — iFood-style single-merchant cart with localStorage persistence.
 * Items: [{ product_id, name, price, quantity, image_url, notes }]
 */
const CartContext = createContext(null);
const STORAGE_KEY = "gf_cart_v2";

function readInitial() {
  if (typeof window === "undefined") return { merchant_id: null, merchant: null, items: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { merchant_id: null, merchant: null, items: [] };
    const saved = JSON.parse(raw);
    return {
      merchant_id: saved.merchant_id || null,
      merchant: saved.merchant || null,
      items: Array.isArray(saved.items) ? saved.items : [],
    };
  } catch {
    return { merchant_id: null, merchant: null, items: [] };
  }
}

export function CartProvider({ children }) {
  // Lazy synchronous hydration — avoids first-paint flicker that hid FloatingCartBar/Badge.
  const initial = readInitial();
  const [merchantId, setMerchantId] = useState(initial.merchant_id);
  const [merchant, setMerchant] = useState(initial.merchant);
  const [items, setItems] = useState(initial.items);

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ merchant_id: merchantId, merchant, items })
      );
    } catch {}
  }, [merchantId, merchant, items]);

  const clear = useCallback(() => {
    setMerchantId(null);
    setMerchant(null);
    setItems([]);
  }, []);

  const addItem = useCallback(
    (product, mch, quantity = 1) => {
      if (merchantId && merchantId !== product.merchant_id) {
        if (!window.confirm("Seu carrinho tem itens de outro restaurante. Esvaziar e adicionar este?")) {
          return false;
        }
        setItems([]);
      }
      setMerchantId(product.merchant_id);
      if (mch) setMerchant({ id: mch.id, business_name: mch.business_name, delivery_fee: mch.delivery_fee, image_url: mch.image_url, delivery_fee_rules: mch.delivery_fee_rules, lat: mch.lat, lng: mch.lng, accept_delivery: mch.accept_delivery, accept_pickup: mch.accept_pickup });
      setItems((prev) => {
        const existing = prev.find((i) => i.product_id === product.id);
        if (existing) {
          return prev.map((i) =>
            i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i
          );
        }
        return [
          ...prev,
          {
            product_id: product.id,
            name: product.name,
            price: Number(product.price || 0),
            quantity,
            image_url: product.image_url,
            notes: "",
          },
        ];
      });
      toast.success(`${product.name} adicionado ao carrinho`, { duration: 1500 });
      return true;
    },
    [merchantId]
  );

  const updateQty = useCallback((productId, newQty) => {
    if (newQty <= 0) {
      setItems((prev) => prev.filter((i) => i.product_id !== productId));
      return;
    }
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity: newQty } : i)));
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const setNotes = useCallback((productId, notes) => {
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, notes } : i)));
  }, []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((acc, i) => acc + i.price * i.quantity, 0);
    const grossFee = Number(merchant?.delivery_fee || 0);
    // Apply merchant subsidy rule (mirror of backend logic for instant UI)
    const rules = merchant?.delivery_fee_rules || {};
    let freeShipping = false;
    if (rules.mode === "always_free") freeShipping = true;
    else if (rules.mode === "free_above" && Number(rules.threshold || 0) > 0 && subtotal >= Number(rules.threshold)) freeShipping = true;
    const deliveryFee = freeShipping ? 0 : grossFee;
    return {
      subtotal,
      deliveryFee,
      grossDeliveryFee: grossFee,
      deliverySubsidy: freeShipping ? grossFee : 0,
      freeShipping,
      total: subtotal + deliveryFee,
      count: items.reduce((acc, i) => acc + i.quantity, 0),
    };
  }, [items, merchant]);

  const value = {
    merchantId,
    merchant,
    items,
    totals,
    addItem,
    updateQty,
    removeItem,
    setNotes,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
